/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import * as templatesClient from 'ContentData/api/Templates';
import Categories from 'ContentUtils/constants/Categories';
import { TemplateSourceFilters } from 'ContentUtils/constants/TemplateSourceFilters';
import { TemplateFilters } from 'ContentUtils/constants/ThemeFilters';
import { ThemeSortingTypes } from 'ContentUtils/constants/ThemeSortingTypes';
import PropTypes from 'prop-types';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { Fragment, useEffect, useState } from 'react';
import { AssetTypes } from 'ContentComponents/constants/AssetTypes';
import { AssetCard } from './AssetCard';
import { EmptyState } from './EmptyState';
import { TemplateFilterBar } from './TemplateFilterBar';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ROOT_FOLDER = 'root';
export const makeQueryParams = (filters, limitPerPage) => {
  const {
    search,
    templateTypeIds,
    themePath,
    page = 1,
    folderName = ROOT_FOLDER,
    sorting = ThemeSortingTypes.MOST_USED,
    sourceFilter = TemplateSourceFilters.ALL,
    type = TemplateFilters.ALL_TEMPLATES,
    hasDndArea = null
  } = filters;
  return {
    sorting,
    templateTypeIds,
    themePath,
    type: type === TemplateFilters.ALL_TEMPLATES ? null : type,
    folderName: folderName === ROOT_FOLDER ? null : folderName,
    sourceFilter: sourceFilter === TemplateSourceFilters.ALL ? null : sourceFilter,
    name: search,
    limit: limitPerPage,
    offset: (page - 1) * limitPerPage,
    excludeUnusedInMemory: !themePath,
    hasDndArea
  };
};
const useFetchTemplates = ({
  filters,
  limitPerPage
}) => {
  const [templates, setTemplates] = useState([]);
  const [templateFolders, setTemplateFolders] = useState([]);
  const [templateCount, setTemplateCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  useEffect(() => {
    let current = true;
    setIsError(false);
    setIsLoading(true);
    templatesClient.fetchTemplates(makeQueryParams(filters, limitPerPage)).then(({
      results: {
        objects,
        total
      },
      folders
    }) => {
      if (current) {
        setTemplates(objects);
        setTemplateFolders(folders);
        setTemplateCount(total);
        setIsLoading(false);
      }
    }, () => {
      if (current) {
        setIsError(true);
        setIsLoading(false);
      }
    });
    return () => {
      current = false;
    };
  }, [filters, limitPerPage]);
  return {
    templates,
    templateCount,
    templateFolders,
    isLoading,
    isError
  };
};
export const TemplateList = ({
  selectedPath,
  filters,
  limitPerPage = 20,
  onSelect,
  onPageChange,
  onSearchChange,
  onSortingChange,
  onSourceFilterChange,
  onFolderNameChange
}) => {
  const {
    templates,
    templateCount,
    templateFolders,
    isLoading,
    isError
  } = useFetchTemplates({
    filters,
    limitPerPage
  });
  const assetType = AssetTypes.TEMPLATE;
  const pageCount = templateCount > 0 ? Math.ceil(templateCount / limitPerPage) : 0;
  if (isError) {
    return /*#__PURE__*/_jsx(EmptyState, {
      assetType: assetType,
      isError: true
    });
  }
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(TemplateFilterBar, {
      sortType: filters.sorting,
      searchValue: filters.search,
      sourceFilter: filters.sourceFilter,
      folderName: filters.folderName,
      templateFolders: templateFolders,
      type: filters.type,
      onSearchChange: onSearchChange,
      onSortingChange: onSortingChange,
      onSourceFilterChange: onSourceFilterChange,
      onFolderNameChange: onFolderNameChange
    }), isLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      layout: "centered",
      minHeight: 300
    }) : /*#__PURE__*/_jsxs(Fragment, {
      children: [templates.length ? /*#__PURE__*/_jsx(CardGrid, {
        columns: 2,
        gutterSize: 5,
        children: templates.map((template, i) => /*#__PURE__*/_jsx(AssetCard, {
          assetType: assetType,
          name: template.displayName || template.label,
          numberInstall: template.categoryId === Categories.blog_post.id ? null : template.numberInstall,
          onClick: () => onSelect(template),
          selected: selectedPath === template.path,
          thumbnailUrl: template.screenshotPath
        }, i))
      }) : /*#__PURE__*/_jsx(EmptyState, {
        assetType: assetType
      }), pageCount > 1 && /*#__PURE__*/_jsx(Paginator, {
        pageCount: pageCount,
        page: filters.page,
        onPageChange: onPageChange,
        marginBlockStart: "600"
      })]
    })]
  });
};
TemplateList.propTypes = {
  selectedPath: PropTypes.string,
  // NOTE: filters is a dependency of the useFetchTemplates effect,
  // passing a new object will cause refetches
  filters: PropTypes.shape({
    folderName: PropTypes.string,
    page: PropTypes.number,
    search: PropTypes.string,
    sorting: PropTypes.oneOf(Object.keys(ThemeSortingTypes)),
    sourceFilter: PropTypes.oneOf(Object.values(TemplateSourceFilters)),
    type: PropTypes.oneOf(Object.values(TemplateFilters)),
    templateTypeIds: PropTypes.arrayOf(PropTypes.number),
    themePath: PropTypes.string,
    hasDndArea: PropTypes.bool
  }),
  limitPerPage: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func,
  onSortingChange: PropTypes.func,
  onPageChange: PropTypes.func,
  onSourceFilterChange: PropTypes.func,
  onFolderNameChange: PropTypes.func
};